// Currency display + rough live FX conversion.
// Astrologers quote in their own currency; the querent can view prices converted to
// their currency (approximate — actual settlement is between the two parties).
export const CURRENCIES = [
  { code: 'HKD', symbol: 'HK$', label: '港幣' },
  { code: 'TWD', symbol: 'NT$', label: '新台幣' },
  { code: 'CNY', symbol: 'RMB', label: '人民幣' },
  { code: 'MOP', symbol: 'MOP', label: '澳門幣' },
  { code: 'USD', symbol: 'US$', label: '美元' },
  { code: 'SGD', symbol: 'S$', label: '新加坡幣' },
  { code: 'MYR', symbol: 'RM', label: '馬幣' },
  { code: 'GBP', symbol: '£', label: '英鎊' },
  { code: 'EUR', symbol: '€', label: '歐元' },
  { code: 'AUD', symbol: 'A$', label: '澳元' },
  { code: 'CAD', symbol: 'C$', label: '加元' },
];

const SYMBOLS = Object.fromEntries(CURRENCIES.map((c) => [c.code, c.symbol]));
export const symbolOf = (code) => SYMBOLS[code] || code || '';

// Legacy astrologer price_currency values (from pricing.js) -> ISO code.
const SYMBOL_TO_CODE = { 'HK$': 'HKD', 'NT$': 'TWD', RMB: 'CNY', MOP: 'MOP', 'US$': 'USD', SGD: 'SGD' };
export const toCode = (v) => (SYMBOLS[v] ? v : (SYMBOL_TO_CODE[v] || 'TWD'));

// Approx rates per 1 USD — fallback only (used if the live fetch fails; "大約就得").
const FALLBACK = { USD: 1, HKD: 7.8, TWD: 32, CNY: 7.2, MOP: 8.0, SGD: 1.35, MYR: 4.7, GBP: 0.79, EUR: 0.92, AUD: 1.5, CAD: 1.36 };

const RATES_KEY = 'fm_fx_rates';

export async function loadRates() {
  try {
    const cached = JSON.parse(localStorage.getItem(RATES_KEY) || 'null');
    if (cached && cached.rates && Date.now() - cached.at < 24 * 3600 * 1000) return cached.rates;
  } catch (e) { /* ignore */ }
  try {
    const res = await fetch('https://open.er-api.com/v6/latest/USD');
    const data = await res.json();
    if (data && data.result === 'success' && data.rates) {
      try { localStorage.setItem(RATES_KEY, JSON.stringify({ rates: data.rates, at: Date.now() })); } catch (e) { /* ignore */ }
      return data.rates;
    }
  } catch (e) { /* ignore */ }
  return FALLBACK;
}

export function convert(amount, from, to, rates) {
  const R = rates || FALLBACK;
  const rf = R[from];
  const rt = R[to];
  if (!rf || !rt) return amount;
  return amount * (rt / rf);
}

const TW = ['台北', '新北', '桃園', '台中', '台南', '高雄', '基隆', '新竹', '嘉義', '苗栗', '彰化', '南投', '雲林', '屏東', '宜蘭', '花蓮', '台東', '澎湖', '金門', '連江', '台灣', '臺'];
const CN = ['北京', '上海', '廣州', '深圳', '成都', '重慶', '杭州', '南京', '武漢', '西安', '天津', '蘇州', '廈門', '福州', '東莞', '佛山', '長沙', '鄭州', '青島', '大連', '瀋陽', '哈爾濱', '昆明', '南寧', '海口', '中國', '大陸'];

export function regionToCurrency(region) {
  const r = String(region || '');
  if (r.includes('香港')) return 'HKD';
  if (r.includes('澳門') || r.includes('澳门')) return 'MOP';
  if (TW.some((c) => r.includes(c))) return 'TWD';
  if (CN.some((c) => r.includes(c))) return 'CNY';
  if (r.includes('新加坡')) return 'SGD';
  if (r.includes('吉隆坡') || r.includes('馬來')) return 'MYR';
  if (r.includes('倫敦')) return 'GBP';
  if (r.includes('多倫多') || r.includes('溫哥華')) return 'CAD';
  if (r.includes('雪梨') || r.includes('墨爾本')) return 'AUD';
  if (r.includes('三藩市') || r.includes('洛杉磯') || r.includes('紐約')) return 'USD';
  return 'HKD';
}

// Format `amount` (quoted in `from`) for display in `to`. Returns { text, approx, orig }.
export function formatPrice(amount, from, to, rates) {
  if (amount === null || amount === undefined) return { text: '', approx: false };
  const f = toCode(from);
  const t = to || f;
  if (!t || t === f) {
    return { text: `${symbolOf(f)} ${Math.round(amount).toLocaleString()}`, approx: false };
  }
  const conv = Math.round(convert(amount, f, t, rates));
  return {
    text: `≈ ${symbolOf(t)} ${conv.toLocaleString()}`,
    approx: true,
    orig: `${symbolOf(f)} ${Math.round(amount).toLocaleString()}`,
  };
}
